import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../../Components/Buttons";
import { FormImage, FormInput, FormSection, FormSelect, FormSwitch } from "../../../Components/Form";
import { ToastMessage } from "../../../Components/Utils";
import BreadCrumb from "../../../Components/BreadCrumb";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { user_profile_image_url } from "../../../Components/ImageUrls";
import { handleError } from "../../../stores/components/handleError";
import { useFetchRolesAllQuery } from "../../../stores/api/roles.api";
import {
  useCreateUnitMutation,
  useUpdateUnitMutation,
} from "../../../stores/api/units.api";
import unitsHelper from "../../../stores/api/helpers/unit.helper";
import { useFetchAllDistrictsQuery } from "../../../stores/api/district.api";
import { useFetchMonitoringOfficersQuery } from "../../../stores/api/users.api";

const CreateUnit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const unitDetails = location?.state?.unit_details;

  const { data: districtsRow = [] } = useFetchAllDistrictsQuery();
  const districts = useMemo(
    () => (districtsRow || []).map((r) => ({ label: r.name, value: r.id })),
    [districtsRow]
  );
  const { data: monitoringOfficersRow = [] } = useFetchMonitoringOfficersQuery();
  const monitoringOfficers = useMemo(
    () => (monitoringOfficersRow || []).map((r) => ({ label: r.name, value: r.id })),
    [monitoringOfficersRow]
  );

  const [createUnit, { isLoading: creating }] = useCreateUnitMutation();
  const [updateUnit, { isLoading: updating }] = useUpdateUnitMutation();
  const loading = creating || updating;

  const [formData, setFormData] = useState({
    name: unitDetails?.name || "",
    district_id: unitDetails?.district_id || "",
    no_of_staff: unitDetails?.no_of_staff ??  0,
    monitoring_officer_id: unitDetails?.monitoring_officer_id || "",
    status: unitDetails?.status ? true : false
  });

  const setField = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));

  const resetForm = () =>
    setFormData({ name: "", district_id: "", no_of_staff: "", district_id: ""});

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (unitDetails) {
      console.log(typeof formData.status);
        await updateUnit(unitsHelper.forUpdate(unitDetails.id, formData)).unwrap();
      } else {
        await createUnit(unitsHelper.forCreate(formData)).unwrap();
      }
      ToastMessage({
        type: "success",
        title: "Operation completed successfully.",
        message: unitDetails ? "Unit updated successfully!" : "Unit created successfully!",
      });
      if (!unitDetails) resetForm();
    } catch (err) {
      handleError(err, "Something went wrong");
    }
  };

  return (
    <div className="p-4">
      <BreadCrumb title={unitDetails ? "Edit Unit" : "Create Unit"} />

      <form className="mx-auto mt-6 dark:bg-slate-900 rounded-lg p-6 space-y-5">
        <FormSection title="Unit Details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="Name"
              isRequired
              name="name"
              value={formData.name}
              onChange={(val) => setField("name", val)}
              placeholder="Enter Unit name"
            />

            <FormSelect
              label="District"
              isRequired
              options={districts}
              value={formData.district_id}
              onChange={(val) => setField("district_id", val ?? "")}
              blurInputOnSelect
            />

            <div>
                <FormInput
                  label="No of Staff"
                  isRequired
                  name="no_of_staff"
                  value={formData.no_of_staff}
                  onChange={(val) => setField("no_of_staff", val)}
                  type="number"
                  placeholder="Enter No Of Staffs"
                />
              
            </div>

            <FormSelect
              label="Monitoring Officer"
              isRequired
              options={monitoringOfficers}
              value={formData.monitoring_officer_id}
              onChange={(val) => setField("monitoring_officer_id", val ?? "")}
              blurInputOnSelect
            />
          </div>
        </FormSection>
        <FormSection title="Status">
            <div className="flex gap-3 items-center">
              <FormSwitch label="Active" checked={formData.status} onChange={(val) => setFormData((p) => ({ ...p, status: Boolean(val) }))} />
            </div>
          </FormSection>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            onClick={() => {
              resetForm();
              navigate(ROUTE_PATHS.UNIT_LIST);
            }}
            className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-800 dark:!text-gray-200"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="!bg-green-500 !text-white hover:!bg-green-600"
            loading={loading}
            disabled={loading}
            onClick={onSubmit}
            loadingText={unitDetails ? "Updating..." : "Creating..."}
          >
            {unitDetails ? "Update Unit" : "Create Unit"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateUnit;
